import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Building2, User, Mail, Calendar, Key, CheckCircle2, Download, Package, Activity, ShieldAlert, Truck, Clock } from 'lucide-react';
import {
  fetchCompanyDetails,
  requestDownloadWarehouses,
  fetchSellerCouriers,
  fetchAvailableCouriers,
} from '../../../../features/sellers/sellerDetailsThunks';
import { clearCompanyDetails } from '../../../../features/sellers/sellerDetailsSlice';
import { describePickupSchedule } from '../../../../features/sellers/sellerFormUtils';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { ChangePasswordModal } from './ChangePasswordModal';
import { SellerCouriersTab } from './components/SellerCouriersTab';
import { selectHasFulfillmentPro } from '../../../../features/auth/authSelectors';
import { PickupScheduleModal } from './components/PickupScheduleModal';
import { DataTable } from '../../../../components/ui/DataTable';

export const SellerDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { company, loading, error, settingCouriers } = useSelector(state => state.sellerDetails);
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [scheduleWarehouse, setScheduleWarehouse] = useState(null);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchCompanyDetails(companyId));
      // Loaded with the page, as the legacy did, so the tab can show its count.
      dispatch(fetchSellerCouriers(companyId));
      dispatch(fetchAvailableCouriers());
    }
    return () => {
      dispatch(clearCompanyDetails());
    };
  }, [companyId, dispatch]);

  const handleDownloadWarehouses = () => {
    if (company?.seller?.id) {
      dispatch(requestDownloadWarehouses(company.seller.id))
        .unwrap()
        .then(() => alert("Se ha solicitado la descarga. Recibirás un correo pronto."))
        .catch(() => alert("Error al solicitar descarga de bodegas."));
    }
  };

  if (loading || !company) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-ninja-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 text-danger p-6 rounded-2xl border border-red-200 dark:border-red-900">
        <h3 className="font-bold text-lg mb-2">Error al cargar</h3>
        <p>{error}</p>
        <button
          onClick={() => navigate('/sellers')}
          className="mt-4 px-4 py-2 bg-white dark:bg-[#1a1a1a] rounded-lg font-medium shadow-sm hover:opacity-90"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  const warehouseColumns = [
    { header: 'ID', accessor: 'id' },
    { header: 'NOMBRE', accessor: 'name' },
    { header: 'ENCARGADO', accessor: 'storekeeper_name' },
    {
      header: 'CONTACTO',
      cell: (row) => (
        <div className="flex flex-col text-sm">
          <span className="font-medium">{row.storekeeper_phone || '-'}</span>
          <span className="text-text-secondary text-xs">{row.storekeeper_email || '-'}</span>
        </div>
      )
    },
    {
      header: 'DIRECCIÓN',
      cell: (row) => `${row.address?.street || ''} ${row.address?.street_number || ''}, ${row.address?.commune || ''}`.trim()
    },
    {
      // "Sin horario" is worth seeing: the courier then collects on its own
      // default hours, which nobody in this company chose.
      header: 'HORARIO DE RETIRO',
      cell: (row) => {
        const schedule = describePickupSchedule(row);
        return (
          <span className={schedule === 'Sin horario' ? 'text-text-muted' : undefined}>
            {schedule}
          </span>
        );
      }
    },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <Button
          variant="subtle"
          size="sm"
          iconOnly
          icon={Clock}
          title="Editar horario de retiro"
          aria-label={`Editar horario de retiro de ${row.name}`}
          data-testid="warehouse-schedule-open"
          onClick={() => setScheduleWarehouse(row)}
        />
      )
    }
  ];

  const integrationColumns = [
    {
      header: 'ECOMMERCE',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <img src={row.image} alt={row.name} className="w-8 h-8 object-contain bg-white rounded-lg p-1 border border-border" />
          <span className="font-semibold capitalize">{row.name}</span>
        </div>
      )
    },
    {
      header: 'TIPO',
      cell: (row) => (
        <span className="px-2 py-1 bg-ninja-greenLight text-ninja-green rounded-md text-xs font-bold uppercase tracking-wider">
          {row.type}
        </span>
      )
    },
    {
      header: 'ESTADO',
      cell: (row) => (
        <div className="flex items-center gap-1.5 text-ninja-green font-medium">
          <CheckCircle2 size={16} />
          <span>Activo</span>
        </div>
      )
    },
    {
      header: 'FECHA ACTIVACIÓN',
      cell: (row) => new Date(row.created_at).toLocaleString()
    }
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-300">

      <DetailBreadcrumb backLabel="Volver a Sellers" to="/sellers" current={company.socialReason} />

      <DetailHero
        media={<Building2 size={40} strokeWidth={1.5} />}
        title={company.socialReason}
        badges={
          <span className="px-3 py-1 bg-ninja-greenLight text-ninja-green rounded-full text-xs font-bold capitalize shadow-sm border border-ninja-green/20">
            {company.plan}
          </span>
        }
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <User size={14} /> {company.fullName} &bull; {company.rut}
            </span>
            <span className="flex items-center gap-1.5 text-ninja-green bg-ninja-green/10 px-2.5 py-1 rounded-lg font-medium">
              <CheckCircle2 size={14} /> Cuenta Activa
            </span>
            <span className="flex items-center gap-1.5 text-text-muted font-medium">
              <Calendar size={14} /> Creado el {new Date(company.createdAt).toLocaleDateString()}
            </span>
          </>
        }
        actions={
          // Kept as it was: the dark call to action has no equivalent among the
          // `Button` variants, and swapping it for a grey one would be a visual
          // change nobody asked for.
          <button
            onClick={() => setIsPasswordModalOpen(true)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-ninja-dark dark:bg-white text-white dark:text-black rounded-xl font-bold hover:opacity-90 transition-opacity shadow-lg"
          >
            <Key size={16} /> Cambiar Contraseña
          </button>
        }
      />

      {/* Tabs Navigation */}
      <div className="flex items-center gap-8 border-b border-border/60">
        {[
          { id: 'general', label: 'Resumen General', icon: Activity },
          { id: 'bodegas', label: 'Bodegas (' + (company.warehouses?.length || 0) + ')', icon: Package },
          { id: 'integraciones', label: 'Integraciones (' + (company.setting_ecommerces?.length || 0) + ')', icon: ShieldAlert },
          // The stand-by couriers are the seller's own carrier list, and
          // choosing carriers is what Fulfillment Pro sells.
          ...(hasFulfillmentPro
            ? [{ id: 'couriers', label: 'Couriers (' + (settingCouriers?.length || 0) + ')', icon: Truck }]
            : []),
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 text-sm font-bold transition-all relative ${isActive ? 'text-ninja-green' : 'text-text-muted hover:text-ninja-dark dark:hover:text-white'
                }`}
            >
              <Icon size={16} className={isActive ? 'text-ninja-green' : 'opacity-60'} />
              {tab.label}
              {isActive && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-ninja-green rounded-t-full shadow-[0_-2px_10px_rgba(0,192,136,0.5)]"></div>
              )}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in slide-in-from-bottom-2 duration-300">

            {/* Info Panel */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
                <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-6 flex items-center gap-2">
                  <Building2 className="text-ninja-green" size={20} /> Datos Comerciales
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">ID Interno</span>
                    <p className="font-medium text-ninja-dark dark:text-white">{company.id}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">KAM Asignado</span>
                    <p className="font-medium text-ninja-dark dark:text-white flex items-center gap-2">
                      {/* A `span`, not a `div`: this lives inside a `<p>`, and
                          React warns that the nesting is invalid HTML. */}
                      <span className="w-6 h-6 rounded-full bg-ninja-greenLight text-ninja-green inline-flex items-center justify-center text-xs font-bold">K</span>
                      {company.kam}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Email Principal</span>
                    <p className="font-medium text-ninja-dark dark:text-white flex items-center gap-2">
                      <Mail size={14} className="text-text-muted" /> {company.seller?.user?.email}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Último Inicio de Sesión</span>
                    <p className="font-medium text-ninja-dark dark:text-white">
                      {company.lastLogin ? new Date(company.lastLogin).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Operators Sidebar */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border h-full flex flex-col">
                <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-4 flex items-center justify-between">
                  Operarios
                  <span className="bg-ninja-greenLight text-ninja-green text-xs px-2.5 py-0.5 rounded-full">{company.operators?.length || 0}</span>
                </h3>

                <div className="flex-1 overflow-y-auto pr-2">
                  {company.operators?.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center text-text-secondary opacity-70 mt-10">
                      <User size={32} className="mb-2 opacity-20" />
                      <p className="text-sm">No hay operarios asignados a este seller.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {company.operators.map(op => (
                        <div key={op.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] border border-transparent hover:border-border transition-colors group cursor-default">
                          <div className="w-10 h-10 rounded-full bg-ninja-gray dark:bg-[#2d2d2d] text-ninja-dark dark:text-white flex items-center justify-center font-bold border border-border group-hover:bg-ninja-greenLight group-hover:text-ninja-green transition-colors">
                            {op.first_name?.[0] || 'O'}
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-ninja-dark dark:text-white">{op.first_name} {op.last_name}</div>
                            <div className="text-xs text-text-secondary">{op.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'bodegas' && (
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden animate-in slide-in-from-bottom-2 duration-300">
            <div className="p-4 border-b border-border flex justify-end">
              <PendingButton
                variant="secondary"
                size="sm"
                icon={Download}
                pendingOn={requestDownloadWarehouses}
                loadingLabel="Descargando..."
                onClick={handleDownloadWarehouses}
              >
                Descargar XLSX
              </PendingButton>
            </div>
            <div className="p-4">
              <DataTable
                columns={warehouseColumns}
                data={company.warehouses || []}
                totalItems={company.warehouses?.length || 0}
                currentPage={1}
                itemsPerPage={100}
                onPageChange={() => { }}
                onRowsPerPageChange={() => { }}
              />
            </div>
          </div>
        )}

        {activeTab === 'integraciones' && (
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden animate-in slide-in-from-bottom-2 duration-300">
            <div className="p-4">
              <DataTable
                columns={integrationColumns}
                data={company.setting_ecommerces || []}
                totalItems={company.setting_ecommerces?.length || 0}
                currentPage={1}
                itemsPerPage={100}
                onPageChange={() => { }}
                onRowsPerPageChange={() => { }}
              />
            </div>
          </div>
        )}

        {activeTab === 'couriers' && <SellerCouriersTab companyId={company.id} />}
      </div>

      <ChangePasswordModal
        sellerId={company.seller?.id}
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />

      <PickupScheduleModal
        isOpen={Boolean(scheduleWarehouse)}
        onClose={() => setScheduleWarehouse(null)}
        warehouse={scheduleWarehouse}
      />
    </div>
  );
};
